import React from 'react';
import {
  Autocomplete,
  TextField,
  createFilterOptions,
  FilterOptionsState,
  AutocompleteRenderInputParams,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import { StudyCategory } from '@aphp-ema/common/study';
import type { StudyFormValues } from '@/features/studies/types/study-edit.interface';
import { useTranslation } from 'react-i18next';
import { Control, FieldErrors } from 'react-hook-form';
import { useCategoryForm } from '@/features/studies/hooks/useCategoryForm';

interface NewCategoryOption {
  inputValue: string;
  label: string;
  isNew: boolean;
}

export type CategorySelectorProps = {
  control: Control<StudyFormValues>;
  errors: FieldErrors<StudyFormValues>;
  loading: boolean;
  disabled?: boolean;
};

type CategoryOption = StudyCategory | NewCategoryOption;

const isStudyCategory = (option: unknown): option is StudyCategory => {
  return option !== null && typeof option === 'object' && 'id' in option;
};

const isNewCategoryOption = (option: unknown): option is NewCategoryOption => {
  return (
    option !== null &&
    typeof option === 'object' &&
    'isNew' in option &&
    Boolean(option['isNew'])
  );
};

const filter = createFilterOptions<CategoryOption>();

const getOptionLabel = (option: CategoryOption | string): string => {
  if (typeof option === 'string') {
    return option;
  }
  if (isNewCategoryOption(option)) {
    return option.label;
  }
  return isStudyCategory(option) ? option.label || '' : '';
};

const isOptionEqualToValue = (
  option: CategoryOption,
  value: CategoryOption,
): boolean => {
  return isStudyCategory(option) && isStudyCategory(value)
    ? option.id === value.id
    : false;
};

export const CategorySelector: React.FC<CategorySelectorProps> = ({
  control,
  errors,
  loading,
  disabled,
}) => {
  const { t } = useTranslation();
  const { autocompleteProps, createCategory } = useCategoryForm();

  const filterOptions = (
    options: CategoryOption[],
    params: FilterOptionsState<CategoryOption>,
  ) => {
    const filtered = filter(options, params);
    const { inputValue } = params;
    const isExisting = options.some(
      (option) => isStudyCategory(option) && inputValue === option.label,
    );
    if (inputValue !== '' && !isExisting) {
      filtered.push({
        inputValue,
        label: t('pages.studies.categorySelector.createOption', {
          value: inputValue,
        }),
        isNew: true,
      });
    }
    return filtered;
  };

  const handleChange = async (
    _: React.SyntheticEvent,
    newValue: string | CategoryOption | null,
  ): Promise<CategoryOption | string> => {
    if (typeof newValue === 'string') {
      const createdCategory = await createCategory(newValue);
      if (createdCategory) {
        return createdCategory;
      }
    } else if (newValue && isNewCategoryOption(newValue)) {
      const createdCategory = await createCategory(newValue.inputValue);
      if (createdCategory) {
        return createdCategory;
      }
    } else if (newValue && isStudyCategory(newValue)) {
      return newValue;
    }
    return '';
  };

  const renderInput = (params: AutocompleteRenderInputParams) => (
    <TextField
      {...params}
      label={t('pages.studies.fields.category')}
      error={!!errors.category}
      helperText={errors.category?.message as string}
    />
  );
  return (
    <Controller
      name="category"
      control={control}
      render={({ field }) => (
        <Autocomplete<CategoryOption, false, false, true>
          {...autocompleteProps}
          {...field}
          freeSolo
          getOptionLabel={getOptionLabel}
          isOptionEqualToValue={isOptionEqualToValue}
          filterOptions={filterOptions}
          onChange={async (_, newValue) => {
            const result = await handleChange(_, newValue);
            field.onChange(result);
          }}
          loading={loading}
          disabled={loading || disabled}
          renderInput={renderInput}
        />
      )}
    />
  );
};
